import React from 'react'
import {StyleSheet, View} from 'react-native'
import {Trans} from '@lingui/macro'
import {useNavigation} from '@react-navigation/native'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {membershipApiClient} from '#/lib/api/membership/client'
import {type CommonNavigatorParams, type NavigationProp} from '#/lib/routes/types'
import {isWeb} from '#/platform/detection'
import {usePremiumApi} from '#/state/premium'
import {useSession} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Check_Stroke2_Corner0_Rounded as CheckIcon} from '#/components/icons/Check'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'PremiumSuccess'>

export function PremiumSuccessScreen({}: Props) {
  const t = useTheme()
  const navigation = useNavigation<NavigationProp>()
  const {currentAccount} = useSession()
  const {setPremiumStatus} = usePremiumApi()

  // Activate premium on mount
  React.useEffect(() => {
    if (currentAccount) {
      // Get URL params that FlexPay passes on redirect
      // FlexPay echoes back custom1 (userDid), custom2 (username), and saleID
      let saleID: string | undefined

      if (isWeb) {
        const params = new URLSearchParams(window.location.search)
        saleID = params.get('saleID') || undefined
        // custom1 and custom2 contain userDid and username but we use currentAccount
      }

      // Calculate expiry (30 days from now for monthly subscription)
      const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()

      // Activate premium on the server
      membershipApiClient
        .activatePremium({
          userDid: currentAccount.did,
          username: currentAccount.handle,
          saleId: saleID,
          expiresAt: expiresAt,
          paymentMethod: 'flexpay',
        })
        .then(() => {
          setPremiumStatus(true, expiresAt, saleID)
        })
        .catch(error => {
          console.error('Failed to activate premium on server:', error)
          // Still set local state as fallback
          setPremiumStatus(true, expiresAt, saleID)
        })
    }
  }, [currentAccount, setPremiumStatus])

  const handleGoHome = React.useCallback(() => {
    navigation.navigate('Home')
  }, [navigation])

  return (
    <Layout.Screen>
      <Layout.Content>
        <View style={[a.flex_1, a.justify_center, a.align_center, a.p_xl, styles.container]}>
          <View style={[styles.iconContainer, {backgroundColor: t.palette.positive_100}]}>
            <CheckIcon size="xl" style={{color: t.palette.positive_600}} />
          </View>

          <Text style={[a.text_2xl, a.font_bold, a.text_center, a.mt_xl, t.atoms.text]}>
            <Trans>Welcome to Premium!</Trans>
          </Text>

          <Text style={[a.text_lg, a.text_center, a.mt_md, t.atoms.text_contrast_medium]}>
            <Trans>Thank you for joining Batesky Premium. You now have access to all premium features.</Trans>
          </Text>

          <Button
            label="Go to Home"
            onPress={handleGoHome}
            size="large"
            variant="solid"
            color="primary"
            style={[a.rounded_md, a.mt_xl, styles.button]}>
            <ButtonText>
              <Trans>Go to Home</Trans>
            </ButtonText>
          </Button>
        </View>
      </Layout.Content>
    </Layout.Screen>
  )
}

const styles = StyleSheet.create({
  container: {
    minHeight: 400,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  button: {
    minWidth: 200,
  },
})
